import { defineStore, } from 'pinia';
import { computed, shallowRef, } from 'vue';
import { nioJsBridge, } from '@nio-wad/universal-bridge-h5';
import type { UserInfoResult, } from '@nio-wad/universal-bridge-h5/dist/types';
import { getEnv, } from '@/utils/env';
import {
  normalizeAccessToken,
  readAccessToken,
  writeAccessToken,
} from '@/utils/tokenStorage';

const ACCESS_TOKEN_TIMEOUT_MS = 5_000;

const resolveWithTimeout = <T>(
  promise: Promise<T>,
  timeout: number
): Promise<T | undefined> => {
  let timer: number | undefined;

  const timeoutPromise = new Promise<undefined>((resolve) => {
    timer = window.setTimeout(() => resolve(undefined), timeout);
  });

  return Promise.race([promise, timeoutPromise,]).finally(() => {
    if (timer !== undefined) {
      window.clearTimeout(timer);
    }
  });
};

export default defineStore('session', () => {
  const accessToken = shallowRef<string>();
  let hostAccessTokenPromise: Promise<string | undefined> | null = null;

  const isLoggedIn = computed(() => {
    // 开发环境默认已登录
    if (import.meta.env.DEV) {
      return true;
    }

    return Boolean(accessToken.value);
  });

  const setAccessToken = (
    token: unknown,
    { persist = true, }: { persist?: boolean } = {}
  ) => {
    const normalizedToken = persist
      ? writeAccessToken(token)
      : normalizeAccessToken(token);

    if (!normalizedToken) {
      return false;
    }

    accessToken.value = normalizedToken;
    return true;
  };

  const getHostAccessToken = async () => {
    try {
      if (nioJsBridge.currentInMPContainer()) {
        // 不传 env 默认是 prod 环境. ( dev, test, stg, prod )
        nioJsBridge.getWSConnect(getEnv());
      }

      const userInfo = await resolveWithTimeout(
        nioJsBridge.getUserInfo(),
        ACCESS_TOKEN_TIMEOUT_MS
      );
      const result = userInfo?.data as UserInfoResult | undefined;

      setAccessToken(result?.access_token, { persist: false, });
    } catch {
      // 宿主能力缺失或调用失败时按未登录继续请求。
    }

    return accessToken.value;
  };

  const getAccessToken = async () => {
    const storedToken = readAccessToken();

    if (storedToken) {
      accessToken.value = storedToken;
      return storedToken;
    }

    if (accessToken.value) {
      return accessToken.value;
    }

    // local环境从配置文件中获取token
    if (setAccessToken(import.meta.env.VITE_API_TOKEN, { persist: false, })) {
      return accessToken.value;
    }

    if (!hostAccessTokenPromise) {
      hostAccessTokenPromise = getHostAccessToken().finally(() => {
        hostAccessTokenPromise = null;
      });
    }

    return hostAccessTokenPromise;
  };

  return {
    isLoggedIn,
    getAccessToken,
    setAccessToken,
  };
});
